import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Search, Loader2, Check, X, ExternalLink, MapPin, Calendar,
  Clock, DollarSign, Tag, Globe, ChevronDown, ChevronUp, Sparkles, CheckCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIEventsTab = ({ token }) => {
  const [regions, setRegions] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedCommunes, setSelectedCommunes] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [errors, setErrors] = useState([]);
  const [approving, setApproving] = useState({});
  const [expandedEvents, setExpandedEvents] = useState({});
  const [editingEvents, setEditingEvents] = useState({});
  const [providerInfo, setProviderInfo] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [searchProgress, setSearchProgress] = useState('');
  const [communesSearched, setCommunesSearched] = useState(0);
  const timerRef = React.useRef(null);

  useEffect(() => {
    fetchTerritories();
    fetchCategories();
    fetchProviderInfo();
  }, []);

  const fetchTerritories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/territories/regions`);
      setRegions(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/categories`);
      setCategories(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchProviderInfo = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/ai-events/provider-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProviderInfo(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleRegionChange = async (regionId) => {
    setSelectedRegion(regionId);
    setSelectedCommunes([]);
    setCommunes([]);
    if (!regionId) return;
    try {
      const res = await axios.get(`${API_URL}/api/territories/communes?region_id=${regionId}`);
      setCommunes(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const toggleCommune = (communeId) => {
    setSelectedCommunes(prev =>
      prev.includes(communeId) ? prev.filter(id => id !== communeId) : [...prev, communeId]
    );
  };

  const selectAllCommunes = () => {
    if (selectedCommunes.length === communes.length) {
      setSelectedCommunes([]);
    } else {
      setSelectedCommunes(communes.map(c => c.id));
    }
  };

  const handleSearch = async () => {
    if (selectedCommunes.length === 0) {
      toast.error('Selecciona al menos una comuna');
      return;
    }
    setSearching(true);
    setResults([]);
    setErrors([]);
    setExpandedEvents({});
    setEditingEvents({});
    setElapsedSeconds(0);
    setCommunesSearched(0);
    setSearchProgress('');

    // Start timer
    timerRef.current = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);

    // Build date payload
    const datePayload = {};
    if (dateFrom) {
      const [y, m, d] = dateFrom.split('-');
      datePayload.date_from = `${d}-${m}-${y}`;
    }
    if (dateTo) {
      const [y, m, d] = dateTo.split('-');
      datePayload.date_to = `${d}-${m}-${y}`;
    }

    // Get commune names for progress display
    const communeNames = {};
    communes.forEach(c => { communeNames[c.id] = c.name; });

    let allEvents = [];
    let allErrors = [];
    let globalIndex = 0;

    // Search one commune at a time for progressive results
    for (let i = 0; i < selectedCommunes.length; i++) {
      const communeId = selectedCommunes[i];
      const communeName = communeNames[communeId] || 'Comuna';
      setSearchProgress(`Buscando en ${communeName}... (${i + 1}/${selectedCommunes.length})`);

      try {
        const res = await axios.post(`${API_URL}/api/admin/ai-events/search`, {
          commune_ids: [communeId],
          ...datePayload
        }, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 180000
        });

        const newEvents = res.data.events || [];
        const newErrors = res.data.errors || [];

        if (newEvents.length > 0) {
          allEvents = [...allEvents, ...newEvents];
          // Update results progressively
          setResults([...allEvents]);
          // Update editing state
          const edits = {};
          allEvents.forEach((ev, idx) => { edits[idx] = { ...ev }; });
          setEditingEvents(edits);
          toast.success(`${newEvents.length} eventos encontrados en ${communeName}`);
        } else {
          toast.info(`Sin eventos nuevos en ${communeName}`);
        }

        allErrors = [...allErrors, ...newErrors];
        if (newErrors.length > 0) setErrors([...allErrors]);

      } catch (err) {
        // Retry once
        try {
          setSearchProgress(`Reintentando ${communeName}...`);
          const res = await axios.post(`${API_URL}/api/admin/ai-events/search`, {
            commune_ids: [communeId],
            ...datePayload
          }, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 180000
          });

          const newEvents = res.data.events || [];
          if (newEvents.length > 0) {
            allEvents = [...allEvents, ...newEvents];
            setResults([...allEvents]);
            const edits = {};
            allEvents.forEach((ev, idx) => { edits[idx] = { ...ev }; });
            setEditingEvents(edits);
            toast.success(`${newEvents.length} eventos encontrados en ${communeName}`);
          }
        } catch (retryErr) {
          allErrors.push(`Error en ${communeName}: ${retryErr.response?.data?.detail || 'Timeout'}`);
          setErrors([...allErrors]);
        }
      }

      setCommunesSearched(i + 1);
    }

    clearInterval(timerRef.current);
    timerRef.current = null;
    setSearching(false);
    setSearchProgress('');
    toast.success(`Busqueda completa: ${allEvents.length} eventos en ${selectedCommunes.length} comunas`);
  };

  const handleApprove = async (index) => {
    const ev = editingEvents[index];
    if (!ev) return;
    setApproving(prev => ({ ...prev, [index]: true }));

    try {
      await axios.post(`${API_URL}/api/admin/ai-events/approve`, {
        name: ev.name,
        description: ev.description,
        start_date: ev.start_date,
        end_date: ev.end_date || ev.start_date,
        address: ev.address,
        latitude: ev.latitude,
        longitude: ev.longitude,
        price: ev.price || 0,
        is_free: ev.is_free !== false,
        category_id: ev.category_id,
        commune_id: ev.commune_id,
        source_url: ev.source_url,
        main_image: ev.main_image,
        schedule: ev.schedule
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`"${ev.name}" aprobado y publicado`);
      // Remove from list
      setResults(prev => prev.filter((_, i) => i !== index));
      const newEdits = { ...editingEvents };
      delete newEdits[index];
      setEditingEvents(newEdits);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al aprobar');
    } finally {
      setApproving(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleApproveAll = async () => {
    const eventsToApprove = Object.values(editingEvents).filter(ev => ev && ev.name);
    if (eventsToApprove.length === 0) return;
    
    setSearching(true);
    try {
      const payload = eventsToApprove.map(ev => ({
        name: ev.name,
        description: ev.description,
        start_date: ev.start_date,
        end_date: ev.end_date || ev.start_date,
        address: ev.address,
        latitude: ev.latitude,
        longitude: ev.longitude,
        price: ev.price || 0,
        is_free: ev.is_free !== false,
        category_id: ev.category_id,
        commune_id: ev.commune_id,
        source_url: ev.source_url,
        main_image: ev.main_image,
        schedule: ev.schedule
      }));
      
      const res = await axios.post(`${API_URL}/api/admin/ai-events/approve-batch`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setResults([]);
      setEditingEvents({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al aprobar lote');
    } finally {
      setSearching(false);
    }
  };

  const handleReject = (index) => {
    setResults(prev => prev.filter((_, i) => i !== index));
    const newEdits = { ...editingEvents };
    delete newEdits[index];
    setEditingEvents(newEdits);
    toast.info('Evento descartado');
  };

  const updateEvent = (index, field, value) => {
    setEditingEvents(prev => ({
      ...prev,
      [index]: { ...prev[index], [field]: value }
    }));
  };

  const toggleExpand = (index) => {
    setExpandedEvents(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? cat.name : catId;
  };

  const filteredResults = results.filter((ev, i) => {
    if (!searchFilter) return true;
    const term = searchFilter.toLowerCase();
    const e = editingEvents[i] || ev;
    return e.name?.toLowerCase().includes(term) || e.commune_name?.toLowerCase().includes(term);
  });

  return (
    <div className="space-y-6" data-testid="ai-events-tab">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Buscar Eventos con IA
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Busca eventos reales en sitios web chilenos. Los eventos encontrados se publican como proveedor PlanCity.
          </p>
          {providerInfo && (
            <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm">
              <p className="font-medium text-gray-700">Credenciales Proveedor PlanCity:</p>
              <p className="text-gray-500">Email: <code className="bg-gray-200 px-1 rounded">{providerInfo.email}</code> | Password: <code className="bg-gray-200 px-1 rounded">{providerInfo.password}</code></p>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {/* Region selector */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Region</label>
              <select
                value={selectedRegion}
                onChange={(e) => handleRegionChange(e.target.value)}
                className="w-full md:w-64 border rounded-lg px-3 py-2 text-sm bg-white"
                data-testid="ai-region-select"
              >
                <option value="">Seleccionar region...</option>
                {regions.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>

            {/* Communes checkboxes */}
            {communes.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Comunas ({selectedCommunes.length}/{communes.length} seleccionadas)
                  </label>
                  <Button variant="ghost" size="sm" onClick={selectAllCommunes} data-testid="select-all-communes">
                    {selectedCommunes.length === communes.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg bg-gray-50">
                  {communes.map(c => (
                    <label
                      key={c.id}
                      className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white rounded p-1.5 transition-colors"
                    >
                      <Checkbox
                        checked={selectedCommunes.includes(c.id)}
                        onCheckedChange={() => toggleCommune(c.id)}
                        data-testid={`commune-check-${c.id}`}
                      />
                      <span className="truncate">{c.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Date range selector */}
            {communes.length > 0 && (
              <div className="mt-2">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Rango de fechas de interes</label>
                <div className="flex flex-wrap items-center gap-3 mt-1.5">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500">Desde</label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="w-40 text-sm"
                      min={new Date().toISOString().split('T')[0]}
                      data-testid="ai-date-from"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500">Hasta</label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="w-40 text-sm"
                      min={dateFrom || new Date().toISOString().split('T')[0]}
                      data-testid="ai-date-to"
                    />
                  </div>
                  {(dateFrom || dateTo) && (
                    <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); }}
                      className="text-xs text-gray-400">
                      Limpiar fechas
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1">Opcional. Si no seleccionas fechas, busca en los proximos 30 dias.</p>
              </div>
            )}

            {/* Search button */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleSearch}
                  disabled={searching || selectedCommunes.length === 0}
                  className="bg-[#064E3B] hover:bg-[#065F46] text-white"
                  data-testid="ai-search-btn"
                >
                  {searching ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Buscando eventos...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Buscar Eventos ({selectedCommunes.length} comunas)
                    </>
                  )}
                </Button>
              </div>
              {searching && (
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-gray-100 rounded-full h-2 max-w-xs overflow-hidden">
                    <div
                      className="bg-[#064E3B] h-full rounded-full transition-all duration-1000"
                      style={{ width: `${Math.min((communesSearched / selectedCommunes.length) * 100, 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-mono text-gray-500 tabular-nums">
                    {Math.floor(elapsedSeconds / 60)}:{(elapsedSeconds % 60).toString().padStart(2, '0')}
                  </span>
                  <span className="text-sm text-gray-500">
                    {searchProgress}
                  </span>
                  {results.length > 0 && (
                    <Badge className="bg-green-100 text-green-700 border-0">
                      {results.length} eventos
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Errors */}
      {errors.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-4">
            {errors.map((err, i) => (
              <p key={i} className="text-sm text-yellow-700">{err}</p>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {results.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-lg">
                {results.length} eventos encontrados
              </CardTitle>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Filtrar resultados..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="w-48"
                  data-testid="ai-results-filter"
                />
                <Button
                  onClick={handleApproveAll}
                  disabled={searching}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="approve-all-btn"
                >
                  <CheckCheck className="w-4 h-4 mr-2" />
                  Aprobar Todos ({results.length})
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {filteredResults.map((ev, originalIndex) => {
              const i = results.indexOf(ev);
              const editing = editingEvents[i] || ev;
              const isExpanded = expandedEvents[i];
              
              return (
                <div
                  key={i}
                  className="border rounded-xl p-4 bg-white hover:shadow-sm transition-shadow"
                  data-testid={`ai-event-${i}`}
                >
                  {/* Header row */}
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-semibold text-gray-900 text-base">{editing.name}</h3>
                        <Badge variant="outline" className="text-xs">{getCategoryName(editing.category_id)}</Badge>
                        {editing.is_free ? (
                          <Badge className="bg-green-100 text-green-700 border-0 text-xs">Gratis</Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">
                            ${editing.price?.toLocaleString('es-CL')} CLP
                          </Badge>
                        )}
                      </div>
                      
                      {/* Key details */}
                      <div className="flex flex-wrap gap-3 text-sm text-gray-500 mb-2">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {editing.commune_name} {editing.address && `- ${editing.address}`}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {editing.start_date ? editing.start_date.split('T')[0].split('-').reverse().join('-') : ''}
                        </span>
                        {editing.schedule && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {editing.schedule}
                          </span>
                        )}
                      </div>

                      {/* Source */}
                      {editing.source_url && (
                        <a
                          href={editing.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                          data-testid={`source-link-${i}`}
                        >
                          <Globe className="w-3 h-3" />
                          {editing.source_name || 'Ver fuente'}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleExpand(i)}
                        className="text-gray-400"
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleReject(i)}
                        data-testid={`reject-event-${i}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => handleApprove(i)}
                        disabled={approving[i]}
                        data-testid={`approve-event-${i}`}
                      >
                        {approving[i] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                        Aprobar
                      </Button>
                    </div>
                  </div>

                  {/* Expanded edit section */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-gray-500 mb-1 block">Nombre</label>
                          <Input
                            value={editing.name || ''}
                            onChange={(e) => updateEvent(i, 'name', e.target.value)}
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 mb-1 block">Categoria</label>
                          <select
                            value={editing.category_id || ''}
                            onChange={(e) => updateEvent(i, 'category_id', e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                          >
                            {categories.map(c => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 mb-1 block">Fecha inicio</label>
                          <Input
                            type="datetime-local"
                            value={editing.start_date?.slice(0, 16) || ''}
                            onChange={(e) => updateEvent(i, 'start_date', e.target.value + ':00')}
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 mb-1 block">Fecha fin</label>
                          <Input
                            type="datetime-local"
                            value={editing.end_date?.slice(0, 16) || ''}
                            onChange={(e) => updateEvent(i, 'end_date', e.target.value + ':00')}
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 mb-1 block">Horario</label>
                          <Input
                            value={editing.schedule || ''}
                            onChange={(e) => updateEvent(i, 'schedule', e.target.value)}
                            placeholder="Ej: 10:00 - 18:00"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 mb-1 block">Precio (CLP)</label>
                          <Input
                            type="number"
                            value={editing.price || 0}
                            onChange={(e) => {
                              const p = parseFloat(e.target.value) || 0;
                              updateEvent(i, 'price', p);
                              updateEvent(i, 'is_free', p === 0);
                            }}
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 mb-1 block">Direccion</label>
                          <Input
                            value={editing.address || ''}
                            onChange={(e) => updateEvent(i, 'address', e.target.value)}
                            className="text-sm"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs font-medium text-gray-500 mb-1 block">Latitud</label>
                            <Input
                              type="number"
                              step="any"
                              value={editing.latitude || ''}
                              onChange={(e) => updateEvent(i, 'latitude', parseFloat(e.target.value))}
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500 mb-1 block">Longitud</label>
                            <Input
                              type="number"
                              step="any"
                              value={editing.longitude || ''}
                              onChange={(e) => updateEvent(i, 'longitude', parseFloat(e.target.value))}
                              className="text-sm"
                            />
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-xs font-medium text-gray-500 mb-1 block">URL Imagen</label>
                          <Input
                            value={editing.main_image || ''}
                            onChange={(e) => updateEvent(i, 'main_image', e.target.value)}
                            placeholder="https://..."
                            className="text-sm"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-xs font-medium text-gray-500 mb-1 block">Fuente</label>
                          <Input
                            value={editing.source_url || ''}
                            onChange={(e) => updateEvent(i, 'source_url', e.target.value)}
                            className="text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 mb-1 block">Descripcion</label>
                        <textarea
                          value={editing.description || ''}
                          onChange={(e) => updateEvent(i, 'description', e.target.value)}
                          rows={3}
                          className="w-full border rounded-lg px-3 py-2 text-sm resize-y"
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AIEventsTab;
